import { usePublicClient } from 'wagmi';
import { useQuery } from '@tanstack/react-query';
import { erc20Abi, formatUnits } from 'viem';
import { UniswapV3OracleABI } from '../components/abi/UniswapV3OracleABI';
import { DynaVaultABI } from '../components/abi/DynavaultABI';
import { getDepositTokenList } from '../utils/depositTokenList';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { getOracleDeployment } from 'sfi-oracle-sdk';

interface UseDepositTokenPriceParams {
  vaultAddress: `0x${string}`;
  vaultChainId: number;
  from: `0x${string}`;
}

const buildPriceCall = (params: {
  tokenAddress: `0x${string}`;
  oracleAddress: `0x${string}`;
  referenceAsset: `0x${string}`;
}) => {
  const { tokenAddress, oracleAddress, referenceAsset } = params;

  return {
    abi: UniswapV3OracleABI,
    functionName: 'getPrice',
    address: oracleAddress,
    args: [tokenAddress, referenceAsset],
  } as const;
};

export const useDepositTokenPrice = (params: UseDepositTokenPriceParams) => {
  const { vaultAddress, vaultChainId, from } = params;

  const publicClient = usePublicClient({
    chainId: vaultChainId,
  });

  return useQuery({
    queryKey: queryKeys.vaultDepositTokenPrice({
      vaultAddress,
      chainId: vaultChainId,
    }),
    refetchInterval: 14000,
    staleTime: 14000,
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('Public client is not available');
      }
      const oracleDeployment = getOracleDeployment(vaultChainId);
      if (!oracleDeployment) {
        throw new Error(
          `No oracle deployment found for chainId ${vaultChainId}`
        );
      }

      // Get vault deposit token address
      const depositTokenAddress = await publicClient.readContract({
        abi: DynaVaultABI,
        account: from,
        address: vaultAddress,
        functionName: 'asset',
        authorizationList: undefined,
      });

      // Get oracle address
      const assetsOracleAddress = await publicClient.readContract({
        abi: DynaVaultABI,
        address: vaultAddress,
        account: from,
        functionName: 'referenceAssetOracle',
        authorizationList: undefined,
      });

      // Get deposit token info from our list
      const depositTokenList = getDepositTokenList(vaultChainId);
      const depositTokenInfo = depositTokenList.find(
        (token) =>
          token.address.toLowerCase() === depositTokenAddress.toLowerCase()
      );

      if (!depositTokenInfo) {
        throw new Error('Deposit token not found in list');
      }

      // Get price from oracle
      const priceCall = buildPriceCall({
        oracleAddress: assetsOracleAddress,
        tokenAddress: depositTokenAddress,
        referenceAsset: oracleDeployment.referenceAsset,
      });

      const [priceResult, referenceDecimals] = await Promise.all([
        publicClient.readContract(priceCall),
        publicClient.readContract({
          abi: erc20Abi,
          address: oracleDeployment.referenceAsset,
          functionName: 'decimals',
          authorizationList: undefined,
        }),
      ]);
      const price = Number(
        formatUnits(priceResult[0] as bigint, referenceDecimals)
      );

      return {
        depositTokenAddress,
        depositTokenInfo,
        price,
        symbol: depositTokenInfo.symbol,
        decimals: depositTokenInfo.decimals,
      };
    },
  });
};
